import React, { useEffect, useMemo, useRef, useState } from "react";
import DayChipSelector from "../common/DayChipSelector";
import { todayString } from "../../utils/dateUtils";
import { plannerAPI, scheduleAPI, tenantAPI } from "../../api/client";

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatAllocDate(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("-").map(Number);
  return `${parts[2]} ${MONTH_SHORT[parts[1] - 1]} ${parts[0]}`;
}

function shiftMatchesEntry(shift, entry) {
  if (!shift || !entry) return false;
  const sortA = [...(shift.workingDays || [])].sort().join(",");
  const sortB = [...(entry.workingDays || [])].sort().join(",");
  return (
    shift.workStartTime === entry.workStartTime &&
    shift.workEndTime === entry.workEndTime &&
    sortA === sortB
  );
}

function parseDateLocal(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function PlannerLeftPanel({
  isAdmin,
  selectedUserId,
  onSelectUser,
  currentUser,
  users,
  selectedUserData,
  currentMonth,
  onRefresh,
}) {
  const [search, setSearch] = useState("");
  const selectedRef = useRef(null);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editWorkingDays, setEditWorkingDays] = useState([]);
  const [editStartTime, setEditStartTime] = useState("09:00");
  const [editEndTime, setEditEndTime] = useState("18:00");
  const [isSaving, setIsSaving] = useState(false);
  const [allocations, setAllocations] = useState([]);

  const filtered = useMemo(() => {
    if (!isAdmin) return users;
    return users.filter((u) =>
      `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [isAdmin, users, search]);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "center" });
    }
  }, [selectedUserId, filtered.length]);

  useEffect(() => {
    if (!selectedUserData) return;
    setEditWorkingDays(selectedUserData.workingDays || []);
    setEditStartTime(selectedUserData.workStartTime || "09:00");
    setEditEndTime(selectedUserData.workEndTime || "18:00");
  }, [selectedUserData, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      setAllocations([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      scheduleAPI.getUser(selectedUserId),
      tenantAPI.getTenant(),
    ]).then(([schedRes, tenantRes]) => {
      if (cancelled) return;
      const history = schedRes.data?.history || [];
      const shifts = Array.isArray(tenantRes.data?.projectFormSchema?.shifts)
        ? tenantRes.data.projectFormSchema.shifts
        : [];
      const shiftColorMap = new Map(shifts.map((s, i) => [s.id, i % 6]));

      const timeline = history
        .map((entry) => {
          if (!entry.effectiveFrom) return null;
          const matched = shifts.find((s) => shiftMatchesEntry(s, entry));
          if (!matched) return null;
          return {
            effectiveFrom: entry.effectiveFrom,
            date: parseDateLocal(entry.effectiveFrom),
            shiftName: matched.name,
            colorIndex: shiftColorMap.get(matched.id) ?? 0,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.date - b.date);

      const ranges = timeline.map((entry, idx) => {
        const next = timeline[idx + 1];
        let end = null;
        if (next) {
          const d = new Date(next.date);
          d.setDate(d.getDate() - 1);
          end = toDateStr(d);
        }
        return { shiftName: entry.shiftName, colorIndex: entry.colorIndex, start: entry.effectiveFrom, end };
      });

      if (!cancelled) setAllocations(ranges);
    }).catch(() => { if (!cancelled) setAllocations([]); });
    return () => { cancelled = true; };
  }, [selectedUserId]);

  return (
    <div className="planner-left">
      {isAdmin && (
        <>
          <div className="planner-search">
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>

          <button
            className={`planner-user-row ${
              selectedUserId === null ? "selected" : ""
            }`}
            onClick={() => onSelectUser(null)}
          >
            {/* <div className="user-avatar all-avatar">??</div> */}
            <div className="user-info">
              <span className="user-name">All Users</span>
              <span className="user-role">Team view</span>
            </div>
          </button>

          <div className="planner-divider" />
        </>
      )}

      {filtered.map((u) => {
        const rowId = u.userId || u.id;
        const isSelected = selectedUserId === rowId;
        return (
          <button
            key={rowId}
            ref={isSelected ? selectedRef : null}
            className={`planner-user-row ${isSelected ? "selected" : ""}`}
            onClick={() => onSelectUser(rowId)}
          >
            <div className="user-avatar">
              {u.firstName?.[0]}
              {u.lastName?.[0]}
            </div>
            <div className="user-info">
              <span className="user-name">
                {u.firstName} {u.lastName}
                {rowId === currentUser?.id && " (You)"}
              </span>
              <span className="user-role">{u.role || ""}</span>
            </div>
          </button>
        );
      })}

      {selectedUserId && (() => {
        const monthStart = parseDateLocal(`${currentMonth.year}-${String(currentMonth.month).padStart(2,"0")}-01`);
        const monthEnd = new Date(currentMonth.year, currentMonth.month, 0); // last day
        const visible = allocations.filter((a) => {
          const start = parseDateLocal(a.start);
          const end = a.end ? parseDateLocal(a.end) : null;
          return start <= monthEnd && (!end || end >= monthStart);
        });
        if (!visible.length) return null;
        return (
        <div className="left-panel-allocations">
          <div className="alloc-section-title">Shift Allocations</div>
          {visible.map((alloc, idx) => (
            <div key={idx} className="alloc-entry">
              <span className={`alloc-dot range-color-${alloc.colorIndex}`} />
              <div className="alloc-content">
                <span className="alloc-shift-name">{alloc.shiftName}</span>
                <span className="alloc-date-range">
                  {formatAllocDate(alloc.start)}
                  {alloc.end ? ` → ${formatAllocDate(alloc.end)}` : " → ongoing"}
                </span>
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {/* {isAdmin && selectedUserId && selectedUserData && (
        <div className="left-panel-schedule">
          <div className="schedule-section-header">
            <span className="schedule-section-title">Work Schedule</span>
            {!isEditingSchedule && (
              <button
                className="edit-schedule-btn"
                onClick={() => setIsEditingSchedule(true)}
              >
                Edit
              </button>
            )}
          </div>

          <p className="schedule-source">
            {selectedUserData.scheduleSource === "USER"
              ? "Custom schedule"
              : "Using org default"}
          </p>

          <DayChipSelector
            selectedDays={
              isEditingSchedule
                ? editWorkingDays
                : selectedUserData.workingDays || []
            }
            onChange={isEditingSchedule ? setEditWorkingDays : () => {}}
            readOnly={!isEditingSchedule}
            mode="workdays"
            label="Working days"
          />

          {isEditingSchedule && (
            <>
              <div className="time-range-editor">
                <div className="form-field">
                  <label className="field-label">Start time</label>
                  <input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    className="time-input"
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">End time</label>
                  <input
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    className="time-input"
                  />
                </div>
              </div>

              <div className="schedule-edit-actions">
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setIsEditingSchedule(false);
                    setEditWorkingDays(selectedUserData.workingDays || []);
                    setEditStartTime(selectedUserData.workStartTime || "09:00");
                    setEditEndTime(selectedUserData.workEndTime || "18:00");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary btn-sm"
                  disabled={isSaving || editWorkingDays.length === 0}
                  onClick={async () => {
                    setIsSaving(true);
                    try {
                      await plannerAPI.updateUserSchedule(selectedUserId, {
                        workingDays: editWorkingDays,
                        workStartTime: editStartTime,
                        workEndTime: editEndTime,
                        effectiveFrom: todayString(),
                      });
                      setIsEditingSchedule(false);
                      onRefresh();
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                >
                  {isSaving ? "Saving..." : "Save Schedule"}
                </button>
              </div>
            </>
          )}
        </div>
      )} */}
    </div>
  );
}
